import { useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import type { LinkPreview, ReactionInfo } from "../../../types/chat";

// Module-level copy of the URL matcher + extractor (the useAndroidShare/fq
// precedent). linkifyMessage in the composition layer keeps its own copy of
// URL_REGEX; here we only need it for the link-preview prefetch effect.
const URL_REGEX = /(https?:\/\/[^\s]+)/gi;
function findUrls(text: string): string[] {
  if (!text) return [];
  const regex = new RegExp(URL_REGEX.source, 'gi');
  const matches: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    matches.push(match[0]);
  }
  return matches;
}

// Structural copy of ChatLayout's local ReactionUser shape (rule 5, no
// back-import): the raw reaction-user records returned by /messages/reactions.
type ReactionUser = { userId?: string | number; name?: string; username?: string; createdAt?: string };

/**
 * Per-message reaction/popover view state (step 6.2).
 *
 * Owns the message-row decoration state the message list renders: the reaction
 * hover-tooltip cache (`reactionInfoByMsg` + its on-demand loader + the hovered
 * reaction + tooltip position), the reaction emoji picker (`pickerFor` + its
 * anchor/menu refs + the click-outside/Escape effect), revealed spoilers, and
 * the link-preview cache (`linkPreviewMap` + the prefetch effect driven by the
 * current channel's messages).
 *
 * The render/action helpers stay in the composition layer and read these values:
 * `linkifyMessage`/`renderLinkPreview` (link rendering), `toggleReaction`
 * (reaction mutation → message cluster, 6.3), and the tooltip JSX. The hovered/
 * picker/spoiler setters are returned so the message list can drive them.
 *
 * Input: `msgs` (the active channel's messages) for the link-preview prefetch.
 */
export function useMessageExtras(msgs: { content?: string }[]) {
  // Link-preview cache, fetched lazily from the URLs in the current messages.
  const [linkPreviewMap, setLinkPreviewMap] = useState<Record<string, LinkPreview | null>>({});
  const linkPreviewMapRef = useRef<Record<string, LinkPreview | null>>({});
  useEffect(() => { linkPreviewMapRef.current = linkPreviewMap; }, [linkPreviewMap]);
  const pendingLinkPreviewRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const urlsToFetch = new Set<string>();
    for (const message of msgs) {
      const found = findUrls(String(message?.content || ''));
      for (const url of found) urlsToFetch.add(url);
    }
    urlsToFetch.forEach((url) => {
      if (!url) return;
      const hasCache = Object.prototype.hasOwnProperty.call(linkPreviewMapRef.current, url);
      if (hasCache || pendingLinkPreviewRef.current.has(url)) return;
      pendingLinkPreviewRef.current.add(url);
      fetch(`/api/link-preview?url=${encodeURIComponent(url)}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const preview = data?.preview && data.preview.url ? (data.preview as LinkPreview) : null;
          setLinkPreviewMap((prev) => ({ ...prev, [url]: preview }));
        })
        .catch(() => {
          setLinkPreviewMap((prev) => ({ ...prev, [url]: null }));
        })
        .finally(() => {
          pendingLinkPreviewRef.current.delete(url);
        });
    });
  }, [msgs]);

  // Reaction hover details cache: messageId -> emoji -> items
  const [reactionInfoByMsg, setReactionInfoByMsg] = useState<Record<string, Record<string, { users: { userId: string; name?: string; username?: string; createdAt?: string }[] }>>>({});
  const [hoverReaction, setHoverReaction] = useState<{ messageId: string; emoji: string } | null>(null);
  const [reactionTipPos, setReactionTipPos] = useState<{ x: number; y: number } | null>(null);

  async function loadReactionsFor(messageId: string) {
    if (reactionInfoByMsg[messageId]) return;
    try {
      const tok = getAuthToken() || '';
      const res = await api.getAuth(`/messages/reactions?messageId=${encodeURIComponent(messageId)}`, tok);
      const map: Record<string, { users: ReactionInfo[] }> = {};
      const entries = Object.entries((res?.reactions ?? {}) as Record<string, ReactionUser[]>) as [string, ReactionUser[]][];
      for (const [emoji, list] of entries) {
        const users = (list || []).map<ReactionInfo>((item) => ({
          userId: String(item?.userId ?? ''),
          name: item?.name ?? undefined,
          username: item?.username ?? undefined,
          createdAt: item?.createdAt,
        }));
        map[emoji] = { users };
      }
      setReactionInfoByMsg(prev => ({ ...prev, [messageId]: map }));
    } catch {
      /* noop */
    }
  }

  const [revealedSpoilers, setRevealedSpoilers] = useState<Record<string, boolean>>({});

  // Reactions palette; using Unicode escapes for portability (used in message action pill)
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  // Reaction picker click-outside handling
  const reactionBtnRef = useRef<HTMLButtonElement | null>(null);
  const reactionMenuRef = useRef<HTMLDivElement | null>(null);
  // Close reaction picker when clicking outside or pressing Escape
  useEffect(() => {
    if (!pickerFor) return;
    const onOutside = (e: Event) => {
      const t = e.target as Node | null;
      const insideBtn = reactionBtnRef.current && t ? reactionBtnRef.current.contains(t) : false;
      const insideMenu = reactionMenuRef.current && t ? reactionMenuRef.current.contains(t) : false;
      if (!insideBtn && !insideMenu) setPickerFor(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPickerFor(null); };
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('touchstart', onOutside, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside, true);
      document.removeEventListener('touchstart', onOutside, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerFor]);

  return {
    linkPreviewMap,
    reactionInfoByMsg,
    loadReactionsFor,
    hoverReaction,
    setHoverReaction,
    reactionTipPos,
    setReactionTipPos,
    revealedSpoilers,
    setRevealedSpoilers,
    pickerFor,
    setPickerFor,
    reactionBtnRef,
    reactionMenuRef,
  };
}
